/**
 * Remembering "Maybe later".
 *
 * An install card on the home dashboard is the moment most people will see
 * it, and the moment it is most annoying if it keeps coming back. Dismissing
 * it holds for 30 days — long enough not to nag, short enough that someone
 * who changes their mind is offered it again without hunting.
 *
 * Account settings stays the permanent path, so forgetting this entirely is
 * never the only way to install.
 */
export const DISMISS_KEY = 'kazi-install-card-dismissed-at';
export const DISMISS_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Pure: given when it was dismissed, should the card show now? */
export const shouldShowInstallCard = (now: number, dismissedAt: number | null): boolean => {
  if (dismissedAt === null) return true;
  // ⚠️ A clock that moved backwards, or a stored value from the future, must
  // not hide the card forever. Anything not in a sane window shows it again.
  if (!Number.isFinite(dismissedAt) || dismissedAt > now) return true;
  return now - dismissedAt >= DISMISS_DAYS * DAY_MS;
};

export const readDismissedAt = (store: Storage | null = safeLocal()): number | null => {
  try {
    const raw = store?.getItem(DISMISS_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null; // private mode, blocked storage — show the card
  }
};

export const noteDismissed = (now: number, store: Storage | null = safeLocal()): void => {
  try { store?.setItem(DISMISS_KEY, String(now)); } catch { /* nothing to do */ }
};

function safeLocal(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}
